import useApiClient from "../../useApiClient";
import { useSelector } from "react-redux";
import { useCallback } from "react";
import { withNetworkErrorHandling } from "../../helpers/withNetworkErrorHandling";

/**
 * API client hook for template operations.
 * Provides methods to get, save, update, and delete templates.
 */
const useTemplatesAPIClient = () => {
  const { get, post, put, remove } = useApiClient();
  const { activeUser } = useSelector((state) => state.user);

  /**
   * Get all templates for the current user
   * @returns {Promise<Array>} Array of templates
   */
  const getTemplates = useCallback(async () => {
    if (!activeUser?.userId) {
      return [];
    }
    const result = await withNetworkErrorHandling(() =>
      get(`users/${activeUser.userId}/templates`),
    );
    return result || [];
  }, [activeUser, get]);

  /**
   * Get a specific template
   * @param {string} templateId - The template ID
   * @returns {Promise<Object|null>} The template or null if not found
   */
  const getTemplate = useCallback(
    async (templateId) => {
      if (!activeUser?.userId) {
        return null;
      }
      return await withNetworkErrorHandling(() =>
        get(`users/${activeUser.userId}/templates/${templateId}`),
      );
    },
    [activeUser, get],
  );

  /**
   * Create a new template
   * @param {Object} templateData - The template data to save
   * @returns {Promise<Object|null>} The saved template or null on error
   */
  const createTemplate = useCallback(
    async (templateData) => {
      if (!activeUser?.userId) {
        return null;
      }
      return await withNetworkErrorHandling(() =>
        post(`users/${activeUser.userId}/templates`, templateData),
      );
    },
    [activeUser, post],
  );

  /**
   * Update an existing template
   * @param {string} templateId - The template ID
   * @param {Object} templateData - The template data to update
   * @returns {Promise<Object|null>} The updated template or null on error
   */
  const updateTemplate = useCallback(
    async (templateId, templateData) => {
      if (!activeUser?.userId) {
        return null;
      }
      return await withNetworkErrorHandling(() =>
        put(`users/${activeUser.userId}/templates/${templateId}`, templateData),
      );
    },
    [activeUser, put],
  );

  /**
   * Delete a template
   * @param {string} templateId - The template ID
   * @returns {Promise<boolean>} True if deleted successfully
   */
  const deleteTemplate = useCallback(
    async (templateId) => {
      if (!activeUser?.userId) {
        return false;
      }
      const result = await withNetworkErrorHandling(() =>
        remove(`users/${activeUser.userId}/templates/${templateId}`),
      );
      return result !== null;
    },
    [activeUser, remove],
  );

  return {
    getTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};

export default useTemplatesAPIClient;
